import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useNotificationStore } from '@/stores/notificationStore';
import { Loader2, Sparkles, Bug, Zap, AlertTriangle, Plus, Pencil, Trash2 } from 'lucide-react';
import { formatDate } from '@/lib/localeFormatters';

interface ChangelogEntry {
  id: string;
  version: string;
  title: string;
  description: string;
  type: 'feature' | 'bugfix' | 'enhancement' | 'breaking';
  published_at: string;
  is_published: boolean;
}

interface ChangelogResponse {
  entries: ChangelogEntry[];
  total: number;
}

interface EntryFormData {
  version: string;
  title: string;
  description: string;
  type: 'feature' | 'bugfix' | 'enhancement' | 'breaking';
  is_published: boolean;
}

const ENTRY_TYPES = [
  { value: 'feature', label: 'Feature' },
  { value: 'bugfix', label: 'Bug Fix' },
  { value: 'enhancement', label: 'Enhancement' },
  { value: 'breaking', label: 'Breaking Change' },
] as const;

const TYPE_CONFIG: Record<string, { label: string; variant: 'primary' | 'error' | 'success' | 'warning'; icon: React.ReactNode }> = {
  feature: { label: 'Feature', variant: 'primary', icon: <Sparkles className="w-3 h-3" /> },
  bugfix: { label: 'Bug Fix', variant: 'error', icon: <Bug className="w-3 h-3" /> },
  enhancement: { label: 'Enhancement', variant: 'success', icon: <Zap className="w-3 h-3" /> },
  breaking: { label: 'Breaking', variant: 'warning', icon: <AlertTriangle className="w-3 h-3" /> },
};

const EMPTY_FORM: EntryFormData = {
  version: '',
  title: '',
  description: '',
  type: 'feature',
  is_published: true,
};

async function fetchChangelog(): Promise<ChangelogResponse> {
  const res = await apiFetch('/api/changelog');
  const json = await res.json();
  if (!json.success) throw new Error(json.message || 'Failed to fetch changelog');
  return json.data;
}

async function fetchAllEntries(): Promise<ChangelogResponse> {
  const res = await apiFetch('/api/admin/changelog');
  const json = await res.json();
  if (!json.success) throw new Error(json.message || 'Failed to fetch');
  return json.data;
}

function groupByVersion(entries: ChangelogEntry[]): Map<string, ChangelogEntry[]> {
  const grouped = new Map<string, ChangelogEntry[]>();
  for (const entry of entries) {
    const existing = grouped.get(entry.version) || [];
    existing.push(entry);
    grouped.set(entry.version, existing);
  }
  return grouped;
}

// formatDate imported from @/lib/localeFormatters

// ─── Form modal ──────────────────────────────────────────────────────

function EntryFormModal({
  isOpen,
  onClose,
  initialData,
  editingId,
}: {
  isOpen: boolean;
  onClose: () => void;
  initialData: EntryFormData;
  editingId: string | null;
}) {
  const [form, setForm] = useState<EntryFormData>(initialData);
  const queryClient = useQueryClient();
  const notify = useNotificationStore();

  React.useEffect(() => {
    if (isOpen) setForm(initialData);
  }, [isOpen, initialData]);

  const createMutation = useMutation({
    mutationFn: async (data: EntryFormData) => {
      const res = await apiFetch('/api/admin/changelog', { method: 'POST', body: JSON.stringify(data) });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || json.message || 'Failed to create');
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-changelog'] });
      queryClient.invalidateQueries({ queryKey: ['changelog'] });
      notify.success('Changelog entry created');
      onClose();
    },
    onError: (err: any) => notify.error(err.message || 'Failed to create entry'),
  });

  const updateMutation = useMutation({
    mutationFn: async (data: Partial<EntryFormData>) => {
      const res = await apiFetch(`/api/admin/changelog/${editingId}`, { method: 'PUT', body: JSON.stringify(data) });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || json.message || 'Failed to update');
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-changelog'] });
      queryClient.invalidateQueries({ queryKey: ['changelog'] });
      notify.success('Changelog entry updated');
      onClose();
    },
    onError: (err: any) => notify.error(err.message || 'Failed to update entry'),
  });

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (editingId) {
      updateMutation.mutate(form);
    } else {
      createMutation.mutate(form);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editingId ? 'Edit Changelog Entry' : 'Add Changelog Entry'}
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
          <Button size="sm" onClick={handleSubmit} isLoading={isSubmitting}>
            {editingId ? 'Save Changes' : 'Create Entry'}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Version</label>
          <input type="text" placeholder="e.g. 1.2.0" value={form.version}
            onChange={(e) => setForm({ ...form, version: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" required />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Title</label>
          <input type="text" placeholder="Short title for this entry" value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" required />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Description</label>
          <textarea placeholder="Detailed description (supports plain text)" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4}
            className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-y" required />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Type</label>
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as EntryFormData['type'] })}
            className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
            {ENTRY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-3">
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" checked={form.is_published}
              onChange={(e) => setForm({ ...form, is_published: e.target.checked })} className="sr-only peer" />
            <div className="w-9 h-5 bg-muted-foreground/40 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
          <span className="text-sm text-foreground">Published</span>
        </div>
      </form>
    </Modal>
  );
}

// ─── Main component ──────────────────────────────────────────────────

interface ChangelogListProps {
  /** When true, shows Add/Edit/Delete controls (for superadmins) */
  editable?: boolean;
}

export function ChangelogList({ editable = false }: ChangelogListProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<ChangelogEntry | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const notify = useNotificationStore();

  // Superadmins see all entries (including drafts); regular users see published only
  const { data, isLoading, error } = useQuery({
    queryKey: editable ? ['admin-changelog'] : ['changelog'],
    queryFn: editable ? fetchAllEntries : fetchChangelog,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch(`/api/admin/changelog/${id}`, { method: 'DELETE' });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || json.message || 'Failed to delete');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-changelog'] });
      queryClient.invalidateQueries({ queryKey: ['changelog'] });
      notify.success('Changelog entry deleted');
      setDeleteConfirmId(null);
    },
    onError: (err: any) => notify.error(err.message || 'Failed to delete'),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-muted-foreground">Failed to load changelog.</p>
      </div>
    );
  }

  const entries = data?.entries ?? [];

  if (entries.length === 0) {
    return (
      <>
        <div className="text-center py-16">
          <p className="text-sm text-muted-foreground mb-3">No changelog entries yet.</p>
          {editable && (
            <Button size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={() => { setEditingEntry(null); setModalOpen(true); }}>
              Create your first entry
            </Button>
          )}
        </div>
        {editable && (
          <EntryFormModal
            isOpen={modalOpen}
            onClose={() => { setModalOpen(false); setEditingEntry(null); }}
            editingId={null}
            initialData={EMPTY_FORM}
          />
        )}
      </>
    );
  }

  const grouped = groupByVersion(entries);

  return (
    <>
      {/* Add New button for superadmins */}
      {editable && (
        <div className="flex justify-end mb-4">
          <Button size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={() => { setEditingEntry(null); setModalOpen(true); }}>
            Add New
          </Button>
        </div>
      )}

      <div className="space-y-8">
        {Array.from(grouped.entries()).map(([version, versionEntries]) => {
          const headerDate = versionEntries.reduce(
            (earliest, e) => (e.published_at < earliest ? e.published_at : earliest),
            versionEntries[0].published_at
          );

          return (
            <div key={version} className="relative">
              <div className="flex items-center gap-3 mb-4">
                <Badge variant="primary" className="text-sm px-3 py-1">v{version}</Badge>
                <span className="text-sm text-muted-foreground">{formatDate(headerDate)}</span>
              </div>

              <div className="space-y-3 ml-1 border-l-2 border-border pl-5">
                {versionEntries.map((entry) => {
                  const config = TYPE_CONFIG[entry.type] || TYPE_CONFIG.feature;
                  return (
                    <div key={entry.id} className="relative group">
                      <div className="absolute -left-[27px] top-1.5 w-2.5 h-2.5 rounded-full bg-muted-foreground/40 border-2 border-background" />

                      <div className="pb-2">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant={config.variant} className="text-xs flex items-center gap-1">
                            {config.icon}
                            {config.label}
                          </Badge>
                          <h4 className="text-sm font-semibold text-foreground">{entry.title}</h4>
                          {editable && !entry.is_published && (
                            <Badge variant="secondary" className="text-xs">Draft</Badge>
                          )}
                          {/* Edit / Delete buttons — visible on hover for superadmins */}
                          {editable && (
                            <div className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => { setEditingEntry(entry); setModalOpen(true); }}
                                className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                                title="Edit"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => setDeleteConfirmId(entry.id)}
                                className="p-1 rounded hover:bg-error-50 text-muted-foreground hover:text-error-600"
                                title="Delete"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">{entry.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create / Edit modal */}
      {editable && (
        <EntryFormModal
          isOpen={modalOpen}
          onClose={() => { setModalOpen(false); setEditingEntry(null); }}
          editingId={editingEntry?.id ?? null}
          initialData={
            editingEntry
              ? { version: editingEntry.version, title: editingEntry.title, description: editingEntry.description, type: editingEntry.type, is_published: editingEntry.is_published }
              : EMPTY_FORM
          }
        />
      )}

      {/* Delete confirmation */}
      {editable && (
        <Modal
          isOpen={!!deleteConfirmId}
          onClose={() => setDeleteConfirmId(null)}
          title="Delete Changelog Entry"
          size="sm"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
              <Button variant="danger" size="sm" isLoading={deleteMutation.isPending}
                onClick={() => deleteConfirmId && deleteMutation.mutate(deleteConfirmId)}>
                Delete
              </Button>
            </div>
          }
        >
          <p className="text-sm text-muted-foreground">Are you sure you want to delete this changelog entry? This action cannot be undone.</p>
        </Modal>
      )}
    </>
  );
}
